import { useCallback, useEffect, useMemo, useState } from 'react';

import { isAfter } from 'date-fns';
import { mapValues } from 'lodash-es';

import { IOffer } from '@rbi-ctg/menu';
import { IOfferRedemptions } from '@rbi-ctg/offers';
import { MenuObjectTypes } from 'enums/menu';
import {
  IEvaluateUserOffersQuery,
  IOfferFeedbackEntryFragment,
  useEvaluateUserOffersQuery
} from 'generated/rbi-graphql';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ServiceMode } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { filterMap } from 'utils/array';
import { platform } from 'utils/environment';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';
import {
  RuleType,
  evaluateRestaurantGroupRestriction,
  getRequiredPlatforms,
  getRequiredServiceModes,
  isRedeemableWithLimit,
  offersFeedbacksById,
  redeemedOnTime,
  reduceOffersFeedback,
  requiresAssignmentWillApply
} from 'utils/offers';
import { getExpirationDateFromRuleset } from 'utils/offers/rule-sets/get-expiration-date';
import { parseSanityObject } from 'utils/sanity';

import { UIPattern } from '../types';

import { LOCALE_BLOCK_KEYS, templateKeyValuePairToObject } from './use-locked-offers';
import { couponIdsToQueryParams, makeRemainingEvaluateUserOfferQueries } from './utils';

export interface IUseOfferListProps {
  serviceMode: null | ServiceMode;
  sortedOffers: IOffer[];
  user: null | UserDetails;
}

const getAllRedeemedOffersNoAuth = (): IOfferRedemptions =>
  LocalStorage.getItem(StorageKeys.OFFERS_REDEEMED_NO_AUTH) || {};

// @todo if we have stored offers and a user signs up afterward,
// should we attribute those redemptions to that user, or allow
// them to redeem the offers again?
const persistUnauthenticatedRedemptions = (offerRedemptions: IOfferRedemptions) => {
  const allRedeemed = getAllRedeemedOffersNoAuth();
  LocalStorage.setItem(StorageKeys.OFFERS_REDEEMED_NO_AUTH, {
    ...allRedeemed,
    ...offerRedemptions
  });
  return allRedeemed;
};

const getCouponIdsFromNonActivationOffers = filterMap<IOffer, string>(
  ({ option }) => {
    const optionType = option && option._type;

    return optionType !== MenuObjectTypes.OFFER_ACTIVATION;
  },
  ({ _id }) => _id
);

/**
 * returns a list of currently usable offers for the given user
 * (if no user is logged in returns all offers)
 */

function useOffersList({ serviceMode, sortedOffers = [], user }: IUseOfferListProps) {
  const preloadedOfferRedemptions = useMemo(getAllRedeemedOffersNoAuth, []);
  const [offers, setOffers] = useState(sortedOffers);
  const { store } = useStoreContext();
  const [redeemedOn, setRedeemedOn] = useState<string>(redeemedOnTime());
  const enablePromoCodeOffers = useFlag(LaunchDarklyFlag.ENABLE_PROMO_CODE_OFFERS);
  const [offersFeedback, setOffersFeedback] = useState<
    Record<string, IOfferFeedbackEntryFragment> | undefined
  >();
  const [unauthenticatedRedemptions, setUnauthenticatedRedemptions] = useState(
    preloadedOfferRedemptions
  );

  /**
   * using Apollo's built-in notifyOnNetworkStatusChange parameter + networkStatus value did not work
   * for tracking the loading state of the query while fetchMore is used.
   * This variable is used instead
   */
  const [isLoadingOrFetchingMore, setIsLoadingOrFetchingMore] = useState(false);

  const cognitoId = user?.cognitoId ?? null;

  const storeId =
    (store.hasSelection ? store.number : store.selectedUnavailableStoreNumber) ?? null;

  // memoize to avoid using a new `redeemedOn` value every render,
  // which would trigger a re-fetch
  const [firstQueryParams, ...restOfQueryParamsList] = useMemo(() => {
    const couponIds = getCouponIdsFromNonActivationOffers(sortedOffers);
    return couponIdsToQueryParams(couponIds, platform(), serviceMode, redeemedOn, storeId);
  }, [redeemedOn, serviceMode, sortedOffers, storeId]);

  // make the first EvaluateUserOffers query, and then trigger the rest in the onCompleted callback
  const { data, loading, fetchMore } = useEvaluateUserOffersQuery({
    context: { shouldNotBatch: true },
    fetchPolicy: 'network-only',
    skip: !sortedOffers.length || !user,
    variables: firstQueryParams,
    partialRefetch: true,
    onCompleted: async () => {
      try {
        await makeRemainingEvaluateUserOfferQueries(fetchMore, restOfQueryParamsList, !!user);
      } catch (error) {
        logger.error(`error during fetchMore on evaluateUserOffers ${error.message}`);
      }
      setIsLoadingOrFetchingMore(false);
    }
  });

  const queryRedeemableOffers = useCallback<() => void>(() => {
    if (cognitoId) {
      setRedeemedOn(redeemedOnTime());
    }
  }, [cognitoId]);

  const redeemUnauthenticatedOffer = useCallback((couponId: string) => {
    setUnauthenticatedRedemptions(redemptions => {
      const redemptionDateTime = redeemedOnTime();
      if (redemptions[couponId] && Array.isArray(redemptions[couponId])) {
        return {
          ...redemptions,
          [couponId]: redemptions[couponId].concat(redemptionDateTime)
        };
      }

      return {
        ...redemptions,
        [couponId]: [redemptionDateTime]
      };
    });
  }, []);

  // we want to avoid displaying offers the user cannot redeem
  // some of this feedback comes from the evaluateUserOffers query
  // and some has to be determined by analyzing the offer's rules
  // and checking if the user has redeemed the offer as an
  // "unauthenticated redemption"
  const shouldDisplayOffer = useCallback(
    (offer: IOffer): boolean => {
      const requiredPlatforms = getRequiredPlatforms(offer.ruleSet);
      const requiredServiceModes = getRequiredServiceModes(offer.ruleSet);

      const isRedeemableAccordingToLimitRule = isRedeemableWithLimit(
        offer.ruleSet,
        unauthenticatedRedemptions[offer._id]
      );

      const shouldIncludeForPlatform = requiredPlatforms.length
        ? requiredPlatforms.includes(platform())
        : true;

      const shouldIncludeForServiceMode =
        !!serviceMode && requiredServiceModes.length
          ? requiredServiceModes.includes(serviceMode)
          : true;

      // Allow to include Promo Code Offers
      const shouldIncludePromoCodeOffers =
        enablePromoCodeOffers && offer.uiPattern === UIPattern.PROMO;

      // Allow to include Standard Offers
      const shouldIncludeStandardOffers =
        !offer.uiPattern || offer.uiPattern === UIPattern.STANDARD;

      // Should only include Standard Offers & Promo Code Offers
      const shouldIncludeForUIPattern = shouldIncludeStandardOffers || shouldIncludePromoCodeOffers;

      // unauthenticated users cannot currently view coupons that
      // require an assignment from braze to use
      const shouldDisplayAssignmentOnly = user ? true : !requiresAssignmentWillApply(offer.ruleSet);

      const shouldDisplayRestaurantRestrictedOffer = evaluateRestaurantGroupRestriction(
        offer.ruleSet,
        storeId
      );

      const shouldIncludeOption = !!offer?.option;

      let endDate: string = '';

      // eslint-disable-next-line no-unused-expressions
      offer.ruleSet?.forEach(rule => {
        if (rule.ruleSetType === RuleType.BetweenDates) {
          endDate = rule.endDate ?? '';
        }

        if (rule.ruleSetType === RuleType.And || rule.ruleSetType === RuleType.Or) {
          rule.ruleSet.forEach(r => {
            if (r.ruleSetType === RuleType.BetweenDates) {
              endDate = r.endDate ?? '';
            }
          });
        }
      });

      if (offer?.expiresOnDatetime) {
        endDate = new Date(offer.expiresOnDatetime).toString();
      }

      const isExpired = isAfter(Date.now(), new Date(endDate));

      const isValidOffer = [
        shouldIncludeForUIPattern,
        shouldIncludeForPlatform,
        shouldIncludeForServiceMode,
        shouldDisplayAssignmentOnly,
        shouldDisplayRestaurantRestrictedOffer,
        isRedeemableAccordingToLimitRule,
        shouldIncludeOption,
        !isExpired
      ].every(Boolean);

      return isValidOffer;
    },
    [enablePromoCodeOffers, serviceMode, storeId, unauthenticatedRedemptions, user]
  );

  const updateFilteredOffersList = useCallback(
    (queryData: IEvaluateUserOffersQuery | undefined, currentUserCognitoId: string | null) => {
      if (!currentUserCognitoId || !queryData) {
        setOffersFeedback(undefined);
        setOffers(sortedOffers.filter(shouldDisplayOffer));
        return;
      }
      let filteredOffers: IOffer[];
      // Get all redeemable user offers in the same order as sortedOffers
      const { offersFeedback: offerFeedbackResult } = queryData.evaluateUserOffers;
      const sortedOffersFeedback: IOfferFeedbackEntryFragment[] = offerFeedbackResult.filter(
        (maybeFeedback): maybeFeedback is IOfferFeedbackEntryFragment => !!maybeFeedback
      );
      const feedbacksById = offersFeedbacksById(sortedOffersFeedback);
      filteredOffers = sortedOffers.reduce<IOffer[]>((currFilteredOffers, offer) => {
        const offerFeedbacks = feedbacksById[offer._id];
        if (!offerFeedbacks) {
          return currFilteredOffers;
        }

        const validFeedbacks = offerFeedbacks.filter(
          ({ redemptionEligibility: { isRedeemable } }) => isRedeemable
        );
        const newOffers = validFeedbacks.map(oneFeedback => {
          let endDate: Date | null;

          // Parse variables Template
          const variables = templateKeyValuePairToObject({
            variables: Object.assign(oneFeedback.offerVariables || {})
          });

          // we use formation end date as default. If formation does not provide, we then use sanity end date.
          if (variables?.endDate) {
            // Need to retrieve the endDate from evaluate offers call
            endDate = new Date(variables.endDate);
          } else {
            // Get Sanity date
            const ruleSet = offer.ruleSet || [];
            endDate = getExpirationDateFromRuleset(ruleSet);
          }

          // Turn all values into string
          // TODO what should the Datetimes look like?
          const variablesStringValues = mapValues(variables, val => (val ? val.toString() : ''));
          const updatedObj = parseSanityObject({
            doc: offer,
            localeBlockKeys: LOCALE_BLOCK_KEYS,
            variables: variablesStringValues
          });

          return {
            ...updatedObj,
            expiresOnDatetime: endDate as Date,
            tokenId: oneFeedback?.tokenId
          };
        });

        // Remove added offers since sortedOffers can potentially have
        // offers with the same offer id.
        delete feedbacksById[offer._id];

        return currFilteredOffers.concat(newOffers);
      }, []);

      filteredOffers = filteredOffers.filter(shouldDisplayOffer);
      const feedback = reduceOffersFeedback(sortedOffersFeedback);
      setOffersFeedback(feedback);
      setOffers(filteredOffers);
    },
    [shouldDisplayOffer, sortedOffers]
  );

  /**
   * track the loading state of ALL evaluateUserOffers requests in progress,
   * from the first useEvaluateUserOffersQuery to the last use of fetchMore
   **/
  useEffect(() => {
    // when the evaluateUserOffers query starts, set this to true as well
    if (loading) {
      setIsLoadingOrFetchingMore(true);
    }
  }, [loading]);

  /**
   * When all EvaluateUserOffer queries have run, call updateFilteredOffersList to update the offers list.
   * If updateFilteredOffersList runs before all offers have been evaluated,
   * the user may be kicked off of the redemption screen if their offer isn't in the list yet.
   **/
  useEffect(() => {
    // do not update filteredOffersList if more EvaluateUserOffers queries are still running
    if (!isLoadingOrFetchingMore) {
      updateFilteredOffersList(data, cognitoId);
    }
  }, [data, updateFilteredOffersList, cognitoId, isLoadingOrFetchingMore]);

  useEffect(() => {
    persistUnauthenticatedRedemptions(unauthenticatedRedemptions);
    setOffers(currentOffers => currentOffers.filter(shouldDisplayOffer));
  }, [unauthenticatedRedemptions]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    evaluateUserOffersLoading: isLoadingOrFetchingMore,
    offers,
    offersFeedback,
    queryRedeemableOffers,
    redeemUnauthenticatedOffer
  };
}

export default useOffersList;
